import React, { useCallback, useMemo, useState } from 'react';
import { useBranding } from '../Context/BrandingContext';
import { getShipments } from '../api/shipments';
import { CONNECT_ROLES } from '../utils/roles';
import { isInsufficientPermissionError } from '../utils/permissionErrors';
import AppHeader from './AppHeader';
import PermissionGate from './PermissionGate';
import { useMarketplaceList } from './marketplace/useMarketplaceList';
import SortableTh from './marketplace/SortableTh';
import { listStyles as s, statusStyle, formatDate } from './marketplace/listStyles';

// Sourced from the Core inscor__Release__c.inscor__Release_Type__c / inscor__Status__c
// picklists (ACON-65 items 9b, 9c).
const TYPE_OPTIONS = [
  'Sales Order', 'Repair Order', 'Credit', 'Purchase Order',
  'Work Order', 'Inventory Transfer', 'Work Package', 'Lease',
];
const STATUS_OPTIONS = [
  'Released', 'In_Picking', 'Picked', 'In QC', 'QCd',
  'In Shipping', 'Shipped', 'Restocking', 'Invoiced', 'Cancelled',
];

const EMPTY_FILTERS = { type: '', status: '', dateFrom: '', dateTo: '', minValue: '', maxValue: '' };

function countActive(f) {
  return [f.type, f.status, f.dateFrom || f.dateTo, f.minValue !== '' || f.maxValue !== ''].filter(Boolean).length;
}

// inscor__Ship_Method__c is returned as a nested { Name } lookup shape.
function refName(value) {
  if (!value) return '—';
  if (typeof value === 'string') return value;
  return value.Name || '—';
}

function fmtCurrency(v) {
  const n = Number(v);
  if (v == null || isNaN(n)) return '—';
  return n.toLocaleString(undefined, { style: 'currency', currency: 'USD' });
}

export default function MyShipments() {
  const { branding } = useBranding();

  const [filtersOpen, setFiltersOpen] = useState(false);
  // `draft` holds what the user is editing; only `applied` drives the query, so
  // several filters can be set before a single refetch is triggered.
  const [draft, setDraft] = useState(EMPTY_FILTERS);
  const [applied, setApplied] = useState(EMPTY_FILTERS);

  const setDraftField = useCallback((key, value) => {
    setDraft((prev) => ({ ...prev, [key]: value }));
  }, []);

  const extraParams = useMemo(() => ({
    type: applied.type || undefined,
    status: applied.status || undefined,
    dateFrom: applied.dateFrom || undefined,
    dateTo: applied.dateTo || undefined,
    minValue: applied.minValue !== '' ? applied.minValue : undefined,
    maxValue: applied.maxValue !== '' ? applied.maxValue : undefined,
  }), [applied]);

  const list = useMarketplaceList({ fetcher: getShipments, requiredConnectRole: CONNECT_ROLES.SHIPMENTS, extraParams });
  const {
    rows, totalCount, totalPages, loading, error,
    isGuest, isPending, hasAccess, roleReady,
    searchInput, setSearchInput, sortBy, setSortBy, page, setPage, refetch,
  } = list;

  if (isGuest) return null;

  const blocked = isPending || (roleReady && !hasAccess) || isInsufficientPermissionError(error);
  const activeFilterCount = countActive(applied);
  const isDirty = Object.keys(EMPTY_FILTERS).some((k) => draft[k] !== applied[k]);
  const canClear = activeFilterCount > 0 || countActive(draft) > 0;
  const hasActiveFilter = !!searchInput || activeFilterCount > 0;
  const isTrulyEmpty = !loading && totalCount === 0 && !hasActiveFilter;
  const hasNoMatches = !loading && totalCount === 0 && hasActiveFilter;

  return (
    <div style={s.page}>
      <AppHeader title="Shipments" />
      <main style={s.main}>
        <div style={s.container}>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
            <button type="button" style={s.refreshBtn} onClick={refetch} disabled={loading} title="Refresh from Salesforce">
              {loading ? '…' : '↻ Refresh'}
            </button>
          </div>

          <div style={s.searchRow}>
            <div style={s.searchWrap}>
              <input
                type="text"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="Search by release #, type, AWB, account, or status…"
                style={s.searchInput}
              />
            </div>
            <button
              type="button"
              style={filtersOpen ? s.filtersToggleBtnActive : s.filtersToggleBtn}
              onClick={() => setFiltersOpen((v) => !v)}
              aria-expanded={filtersOpen}
            >
              Filters{activeFilterCount > 0 ? ` (${activeFilterCount})` : ''}
            </button>
          </div>

          {filtersOpen && (
            <div style={s.filterPanel}>
              <div style={s.filterField}>
                <label style={s.filterLabel}>Release Type</label>
                <select value={draft.type} onChange={(e) => setDraftField('type', e.target.value)} style={s.filterSelect}>
                  <option value="">All types</option>
                  {TYPE_OPTIONS.map((opt) => <option key={opt} value={opt}>{opt}</option>)}
                </select>
              </div>
              <div style={s.filterFieldCompact}>
                <label style={s.filterLabel}>Status</label>
                <select value={draft.status} onChange={(e) => setDraftField('status', e.target.value)} style={s.compactSelect}>
                  <option value="">All statuses</option>
                  {STATUS_OPTIONS.map((opt) => <option key={opt} value={opt}>{opt.replace(/_/g, ' ')}</option>)}
                </select>
              </div>
              <div style={s.filterField}>
                <label style={s.filterLabel}>Release Date range</label>
                <div style={s.rangeRow}>
                  <input type="date" value={draft.dateFrom} onChange={(e) => setDraftField('dateFrom', e.target.value)} style={s.dateRangeInput} />
                  <span>–</span>
                  <input type="date" value={draft.dateTo} onChange={(e) => setDraftField('dateTo', e.target.value)} style={s.dateRangeInput} />
                </div>
              </div>
              <div style={s.filterField}>
                <label style={s.filterLabel}>Release Value range</label>
                <div style={s.rangeRow}>
                  <input type="number" placeholder="Min" value={draft.minValue} onChange={(e) => setDraftField('minValue', e.target.value)} style={s.rangeInput} />
                  <span>–</span>
                  <input type="number" placeholder="Max" value={draft.maxValue} onChange={(e) => setDraftField('maxValue', e.target.value)} style={s.rangeInput} />
                </div>
              </div>
              <div style={s.filterActions}>
                <button
                  type="button"
                  style={canClear ? s.clearFiltersBtn : s.clearFiltersBtnDisabled}
                  disabled={!canClear}
                  onClick={() => { setDraft(EMPTY_FILTERS); setApplied(EMPTY_FILTERS); }}
                >
                  Clear
                </button>
                <button
                  type="button"
                  style={isDirty ? s.applyFiltersBtn : s.applyFiltersBtnDisabled}
                  disabled={!isDirty}
                  onClick={() => setApplied(draft)}
                >
                  Apply Filters
                </button>
              </div>
            </div>
          )}

          {loading && <p style={s.muted}>Loading shipments&hellip;</p>}

          <PermissionGate blocked={blocked} primaryColor={branding.primaryColor} minHeight={320}>
            {isTrulyEmpty && (
              <div style={s.emptyCard}><p style={s.muted}>No shipments found for your account.</p></div>
            )}
            {hasNoMatches && (
              <div style={s.emptyCard}><p style={s.muted}>No shipments match your search or filters.</p></div>
            )}

            {!loading && rows.length > 0 && (
              <div style={s.tableWrap}>
                <table style={s.table}>
                  <thead>
                    <tr>
                      <SortableTh label="Release Type" sortKey="type" sortBy={sortBy} onSort={(v) => { setSortBy(v); setPage(1); }} />
                      <th style={s.th}>Status</th>
                      <th style={s.th}>Order Number</th>
                      <SortableTh label="Release Date" sortKey="date" sortBy={sortBy} onSort={(v) => { setSortBy(v); setPage(1); }} />
                      <SortableTh label="Release Value" sortKey="value" sortBy={sortBy} onSort={(v) => { setSortBy(v); setPage(1); }} />
                      <th style={s.th}>Ship Method</th>
                      <th style={s.th}>Acc #</th>
                      <th style={s.th}>AWB</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r, idx) => {
                      const st = statusStyle(r.inscor__Status__c);
                      return (
                        <tr key={r.Id || idx} style={idx % 2 === 0 ? s.rowEven : undefined}>
                          <td style={s.td}>{r.inscor__Release_Type__c || '—'}</td>
                          <td style={s.td}>
                            <span style={{ ...s.statusBadge, color: st.color, backgroundColor: st.bg }}>{st.label}</span>
                          </td>
                          <td style={s.td}>{r.OrderNumber || '—'}</td>
                          <td style={s.td}>{formatDate(r.inscor__Release_Date__c)}</td>
                          <td style={s.td}>{fmtCurrency(r.inscor__Release_Value__c)}</td>
                          <td style={s.td}>{refName(r.inscor__Ship_Method__c)}</td>
                          <td style={s.td}>{r.inscor__Acct__c || '—'}</td>
                          <td style={s.td}>{r.inscor__AWB__c || '—'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </PermissionGate>

          {!loading && totalCount > 0 && (
            <div style={s.pager}>
              <button type="button" style={page <= 1 ? s.pagerBtnDisabled : s.pagerBtn} disabled={page <= 1} onClick={() => setPage(page - 1)}>Previous</button>
              <span style={s.pagerInfo}>Page {page} of {totalPages} ({totalCount} shipment{totalCount === 1 ? '' : 's'})</span>
              <button type="button" style={page >= totalPages ? s.pagerBtnDisabled : s.pagerBtn} disabled={page >= totalPages} onClick={() => setPage(page + 1)}>Next</button>
            </div>
          )}

          {error && !isInsufficientPermissionError(error) && (
            <p style={s.err}>{error.message || 'Failed to load shipments'}</p>
          )}
        </div>
      </main>
    </div>
  );
}
